import React from 'react';
import { generatePaymentPDF } from './paymentPdfUtils';
import { getImageUrl } from '../utils/imageUtils';

const PaymentVoicePopup = ({ isOpen, onClose, payment }) => {
  if (!isOpen || !payment) return null;

  // Get the image URL with base URL handling
  const patientImage = payment.patient?.image || payment.image;
  const imageUrl = patientImage ? getImageUrl(patientImage) : `https://i.pravatar.cc/150?img=1`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Payment Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content */}
        <div className="p-6">
          {/* Patient Info */}
          <div className="flex items-center mb-4">
            <img
              className="h-12 w-12 rounded-full object-cover mr-4 border-2 border-gray-200"
              src={imageUrl}
              alt={payment.patient?.name || payment.patientName || payment.name || 'Patient'}
            />
            <div>
              <h3 className="font-semibold text-gray-900">
                {payment.patient?.name || payment.patientName || payment.name || 'Unknown Patient'}
              </h3>
              <p className="text-sm text-gray-500">
                {payment.patient?.email || payment.patientEmail || payment.email || 'No email'}
              </p>
            </div>
          </div>
          {/* Payment Details */}
          <div className="space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-gray-600">Payment Type:</span>
              <span className="font-medium text-gray-900">{payment.paymentType || payment.type || 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Amount:</span>
              <span className="font-semibold text-gray-900">
                {payment.amount ?
                  (typeof payment.amount === 'string' ?
                    `${payment.currency?.toUpperCase() || 'AUD'} $${payment.amount}` :
                    new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: payment.currency?.toUpperCase() || 'AUD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    }).format(payment.amount)) :
                  'AUD $0'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Method:</span>
              <span className="text-gray-900">{payment.method || payment.paymentMethod || 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                (payment.status || '').toLowerCase() === 'completed' ? 'bg-green-100 text-green-800' :
                (payment.status || '').toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                (payment.status || '').toLowerCase() === 'failed' ? 'bg-red-100 text-red-800' :
                (payment.status || '').toLowerCase() === 'refunded' ? 'bg-purple-100 text-purple-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                  (payment.status || '').toLowerCase() === 'completed' ? 'bg-green-500' :
                  (payment.status || '').toLowerCase() === 'pending' ? 'bg-yellow-500' :
                  (payment.status || '').toLowerCase() === 'failed' ? 'bg-red-500' :
                  (payment.status || '').toLowerCase() === 'refunded' ? 'bg-purple-500' :
                  'bg-gray-500'
                }`} />
                {(payment.status || 'Unknown').charAt(0).toUpperCase() + (payment.status || 'Unknown').slice(1)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="text-gray-900">
                {payment.date ? new Date(payment.date).toLocaleDateString() : 'No date'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Transaction ID:</span>
              <span className="text-gray-900 text-xs font-mono">
                {payment._id || payment.id || 'N/A'}
              </span>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={() => generatePaymentPDF(payment)}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentVoicePopup;